import { useMemo } from "react";
import { CyberPanel } from "@/components/ui/CyberPanel";
import { SEO } from "@/components/seo/SEO";
import { usePublicPartners } from "@/features/partners/hooks";
import type { Partner } from "@/features/partners/types";
import { PublicError, PublicLoading } from "@/pages/public/PublicState";
import { PartnerGrid } from "./PartnerGrid";

function sortPartners(partners: Partner[]) {
  return [...partners].sort((a, b) => {
    if (a.featured !== b.featured) return a.featured ? -1 : 1;
    return a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);
  });
}

export function PartnersPage() {
  const query = usePublicPartners();

  const partners = useMemo(() => sortPartners(query.data ?? []), [query.data]);
  const featuredCount = partners.filter((partner) => partner.featured).length;

  if (query.isLoading) return <PublicLoading label="Loading partners..." />;
  if (query.isError) return <PublicError label="Unable to load partners." />;

  return (
    <section className="mx-auto w-full max-w-7xl px-3 py-8 sm:px-6 sm:py-12 lg:px-8">
      <SEO title="Partners" description="Active Kalaco partners and trusted connections." />
      <CyberPanel>
        <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-teal-200 sm:text-sm sm:tracking-[0.28em]">Partners</p>
            <h1 className="mt-3 break-words font-display text-3xl font-bold uppercase text-white sm:text-5xl">
              Trusted Connections
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-content-secondary sm:text-lg sm:leading-8">
              Active partners pulled from the CMS, ordered with featured partners first.
            </p>
          </div>
          <div className="w-full shrink-0 rounded border border-cyan-300/20 bg-black/40 px-4 py-3 text-sm text-content-secondary lg:w-auto">
            <span className="font-bold text-cyan-100">{featuredCount}</span> featured / {partners.length} active
          </div>
        </div>
      </CyberPanel>

      <div className="mt-8">
        <PartnerGrid partners={partners} />
      </div>
    </section>
  );
}
